import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase";
import { invalidateFor } from "@/lib/cache-registry";
import { OptimisticLockConflictError } from "@/lib/optimistic-lock";
import { useAuth } from "@/contexts/AuthContext";
import type { DBOfferWorkspace } from "@/lib/db-types";
import type { AiSourceChoice } from "@/lib/tilbudsarbeidsrom/ai-sources";

/** Grunnlaget for ett tilbud (VIB-2979). null til noe er lagret. */
export function useOfferWorkspace(offerId: string | undefined) {
  return useQuery({
    queryKey: ["offer-workspace", offerId],
    enabled: !!offerId,
    queryFn: async (): Promise<DBOfferWorkspace | null> => {
      const { data, error } = await supabase
        .from("offer_workspaces")
        .select("*")
        .eq("offer_id", offerId!)
        .maybeSingle();
      if (error) throw error;
      return (data as DBOfferWorkspace | null) ?? null;
    },
  });
}

export interface OfferWorkspacePatch {
  target_audience?: string | null;
  budget_frame_nok?: number | null;
  ai_sources?: AiSourceChoice[];
}

/**
 * Lagrer Grunnlag med optimistisk lås på `updated_at`. Første lagring setter
 * inn raden; finnes den allerede (en kollega lagret først), er det også en
 * konflikt. Aktør er den ekte innloggede brukeren, også under impersonering.
 * Returnerer ny `updated_at`, så neste lagring kan bruke den før spørringen
 * er hentet på nytt.
 */
export function useSaveOfferWorkspace(offerId: string) {
  const qc = useQueryClient();
  const { user: authUser } = useAuth();

  return useMutation({
    mutationFn: async ({
      patch,
      expectedUpdatedAt,
    }: {
      patch: OfferWorkspacePatch;
      expectedUpdatedAt: string | null;
    }): Promise<string> => {
      const actor = authUser?.id ?? null;
      if (expectedUpdatedAt === null) {
        const { data, error } = await supabase
          .from("offer_workspaces")
          .insert({ offer_id: offerId, ...patch, created_by: actor, updated_by: actor })
          .select("updated_at")
          .single();
        if (error?.code === "23505") throw new OptimisticLockConflictError("offer_workspaces", offerId);
        if (error) throw error;
        return (data as { updated_at: string }).updated_at;
      }
      const { data, error } = await supabase
        .from("offer_workspaces")
        .update({ ...patch, updated_by: actor })
        .eq("offer_id", offerId)
        .eq("updated_at", expectedUpdatedAt)
        .select("updated_at");
      if (error) throw error;
      if (!data || data.length === 0) throw new OptimisticLockConflictError("offer_workspaces", offerId);
      return (data[0] as { updated_at: string }).updated_at;
    },
    onSuccess: () => invalidateFor(qc, "offer_workspaces", { offerId }),
    onError: (err) => {
      if (err instanceof OptimisticLockConflictError) {
        toast.error("Noen andre har endret grunnlaget. Det er lastet på nytt, prøv igjen.");
        invalidateFor(qc, "offer_workspaces", { offerId });
        return;
      }
      toast.error(`Kunne ikke lagre grunnlaget: ${err instanceof Error ? err.message : "ukjent feil"}`);
    },
  });
}
